<template>
    <div class="setting-page-content-wrapper profile-page">
        <!-- Profile Header -->
        <div class="profile-header">
            <div class="profile-header__avatar">
                <label class="avatar-upload">
                    <a-avatar
                        v-if="$_.get(user, 'gravatar') === null && gravatar === null"
                        :size="80"
                        icon="user"
                    />
                    <a-avatar v-else :size="80" :src="gravatar || $_.get(user, 'gravatar')" />
                    <input id="profile-avatar" class="d-none" type="file" @change="uploadFile" />
                    <div class="avatar-overlay">
                        <md-icon>photo_camera</md-icon>
                    </div>
                </label>
            </div>
            <div class="profile-header__info">
                <h2 class="profile-header__name">{{ user.name }}</h2>
                <p class="profile-header__email">{{ user.email }}</p>
                <div class="profile-header__badges">
                    <a-tag color="blue">
                        <a-icon type="user" /> {{ $t("message.profile") }}
                    </a-tag>
                    <a-tag v-if="user.isOnline" color="green">
                        <a-icon type="check-circle" /> {{ $t("message.online") }}
                    </a-tag>
                </div>
            </div>
        </div>

        <!-- Settings Tabs -->
        <a-tabs v-model="activeTab" class="profile-tabs">
            <!-- Profile & Language Tab -->
            <a-tab-pane key="profile">
                <span slot="tab">
                    <a-icon type="user" />
                    {{ $t("message.profile") }}
                </span>
                <div class="tab-content">
                    <div class="settings-section">
                        <h3 class="section-title">
                            <a-icon type="edit" />
                            {{ $t("message.username") }}
                        </h3>
                        <a-form layout="vertical">
                            <a-form-item :label="$t('message.username')">
                                <a-input v-model="userName">
                                    <a-icon slot="prefix" type="user" />
                                </a-input>
                            </a-form-item>
                            <a-form-item>
                                <a-button type="primary" icon="save" @click="changeUserName">
                                    {{ $t("message.updateUserName") }}
                                </a-button>
                            </a-form-item>
                        </a-form>
                    </div>

                    <a-divider />

                    <div class="settings-section">
                        <h3 class="section-title">
                            <a-icon type="global" />
                            {{ $t("message.languageSettings") }}
                        </h3>
                        <a-form layout="vertical">
                            <a-form-item :label="$t('message.languageSettings')">
                                <a-select v-model="lang" style="width: 250px;">
                                    <a-select-option v-for="lng in languages" :key="lng.code" :value="lng.code">
                                        {{ lng.originalName }}
                                    </a-select-option>
                                </a-select>
                            </a-form-item>
                        </a-form>
                    </div>
                </div>
            </a-tab-pane>

            <!-- Password Tab -->
            <a-tab-pane key="password">
                <span slot="tab">
                    <a-icon type="lock" />
                    {{ $t("message.changePassword") }}
                </span>
                <div class="tab-content">
                    <div class="settings-section">
                        <h3 class="section-title">
                            <a-icon type="lock" />
                            {{ $t("message.changePassword") }}
                        </h3>
                        <a-form layout="vertical" class="password-form">
                            <a-form-item :label="$t('message.oldPassword')">
                                <a-input-password v-model="oldPassword">
                                    <a-icon slot="prefix" type="key" />
                                </a-input-password>
                            </a-form-item>
                            <a-form-item :label="$t('message.newPassword')">
                                <a-input-password v-model="newPassword">
                                    <a-icon slot="prefix" type="lock" />
                                </a-input-password>
                            </a-form-item>
                            <a-form-item :label="$t('message.passwordRepeat')">
                                <a-input-password v-model="passwordConfirmation">
                                    <a-icon slot="prefix" type="lock" />
                                </a-input-password>
                            </a-form-item>
                            <a-form-item>
                                <a-button type="primary" icon="save" @click="changePassword">
                                    {{ $t("message.changePassword") }}
                                </a-button>
                            </a-form-item>
                        </a-form>
                    </div>
                </div>
            </a-tab-pane>

            <!-- Notification Settings Tab -->
            <a-tab-pane key="notifications">
                <span slot="tab">
                    <a-icon type="bell" />
                    {{ $t("message.soundSettings") }}
                </span>
                <div class="tab-content">
                    <div class="settings-section">
                        <h3 class="section-title">
                            <a-icon type="sound" />
                            {{ $t("message.soundSettings") }}
                        </h3>
                        <div class="sound-settings-grid">
                            <div
                                v-for="(setting, index) in notificationSettings"
                                :key="index"
                                class="sound-setting-item"
                            >
                                <div class="sound-setting-item__header">
                                    <span class="sound-setting-item__title">
                                        {{ $t("message." + getNotificationLabel(setting.type)) }}
                                    </span>
                                </div>
                                <div class="sound-setting-item__controls">
                                    <a-select
                                        v-model="setting.soundFile"
                                        size="small"
                                        style="width: 140px"
                                        @change="playPreviewSound(setting)"
                                    >
                                        <a-select-option v-for="s in soundFiles" :key="s" :value="s">
                                            {{ s }}
                                        </a-select-option>
                                    </a-select>
                                    <div class="volume-control">
                                        <a-slider
                                            v-model="setting.audioLevel"
                                            :min="0"
                                            :max="100"
                                            :step="10"
                                            style="width: 100px"
                                            @afterChange="playPreviewSound(setting)"
                                        />
                                        <span class="volume-label">{{ setting.audioLevel }}%</span>
                                    </div>
                                    <a-button
                                        type="primary"
                                        shape="circle"
                                        icon="play-circle"
                                        size="small"
                                        @click="playPreviewSound(setting)"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <a-divider />

                    <div class="settings-section">
                        <h3 class="section-title">
                            <a-icon type="notification" />
                            {{ $t("message.desktopNotifications") }}
                        </h3>
                        <div class="desktop-notifications-grid">
                            <div
                                v-for="(setting, index) in desktopNotificationSettings"
                                :key="index"
                                class="notification-toggle-item"
                            >
                                <div class="notification-toggle-item__info">
                                    <a-icon :type="getNotificationIcon(setting.type)" class="notification-icon" />
                                    <span>{{ $t("message." + getNotificationLabel(setting.type)) }}</span>
                                </div>
                                <a-switch v-model="setting.desktopNotification" />
                            </div>
                        </div>
                    </div>
                </div>
            </a-tab-pane>

            <!-- Sessions Tab -->
            <a-tab-pane key="sessions">
                <span slot="tab">
                    <a-icon type="laptop" />
                    {{ $t("sessions.mySessions") }}
                </span>
                <div class="tab-content">
                    <div class="sessions-header">
                        <div>
                            <h3 class="section-title">
                                <a-icon type="laptop" />
                                {{ $t("sessions.mySessions") }}
                            </h3>
                            <p class="section-desc">{{ $t("sessions.mySessionsDesc") }}</p>
                        </div>
                        <div class="sessions-actions">
                            <a-button icon="reload" @click="fetchMySessions" :loading="sessionsLoading">
                                {{ $t("message.refresh") }}
                            </a-button>
                            <a-button
                                type="danger"
                                ghost
                                icon="disconnect"
                                @click="handleRevokeAll"
                                :disabled="otherSessionsCount === 0"
                            >
                                {{ $t("sessions.revokeAllOther") }}
                            </a-button>
                        </div>
                    </div>

                    <div v-if="sessionsLoading" class="sessions-loading">
                        <a-spin size="large" />
                    </div>

                    <div v-else-if="mySessions.length === 0" class="sessions-empty">
                        <a-empty :description="$t('sessions.noActiveSessions')" />
                    </div>

                    <div v-else class="sessions-list">
                        <SessionCard
                            v-for="session in mySessions"
                            :key="session._id"
                            :session="session"
                            @revoke="handleRevoke"
                        />
                    </div>
                </div>
            </a-tab-pane>
        </a-tabs>
    </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex";
import { sendSocketMessage } from "../../../utils/socketStream";
import { uploadAsset } from "../../../api/assetUpload";
import axios from "axios";
import * as _ from "lodash";
import languages from "../../../language/languages.json";
import SessionCard from "../../../components/session/SessionCard.vue";

export default {
    name: "Profile",
    components: {
        SessionCard,
    },
    data() {
        return {
            activeTab: "profile",
            languages,
            gravatar: null,
            userName: "",
            oldPassword: "",
            newPassword: "",
            passwordConfirmation: "",
            isFirstOpening: true,
            notificationSettings: [
                { type: "new_visitor", soundFile: "new-conversation-default", audioLevel: 100, desktopNotification: false },
                { type: "new_chat", soundFile: "exquisite", audioLevel: 100, desktopNotification: false },
                { type: "new_conversation", soundFile: "exquisite", audioLevel: 100, desktopNotification: false },
                { type: "new_message", soundFile: "exquisite", audioLevel: 100, desktopNotification: false },
                { type: "team_chat_team", soundFile: "exquisite", audioLevel: 100, desktopNotification: false },
                { type: "team_chat_dm", soundFile: "exquisite", audioLevel: 100, desktopNotification: false },
                { type: "team_chat_department", soundFile: "exquisite", audioLevel: 100, desktopNotification: false },
                { type: "team_chat_group", soundFile: "exquisite", audioLevel: 100, desktopNotification: false },
                { type: "new_announcement", soundFile: "exquisite", audioLevel: 100, desktopNotification: false },
                { type: "ticket_sound", soundFile: "exquisite", audioLevel: 100, desktopNotification: false },
            ],
            soundFiles: [
                "done-for-you", "exquisite", "for-sure", "hey-champ", "juntos",
                "just-saying", "oringz", "percussion-sound", "plucky", "sharp",
                "swiftly", "take-this", "pm-default", "new-message-default",
                "new-conversation-default", "presto", "telefono-viejo"
            ],
        };
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            site: (state) => state.site.site,
            mySessions: (state) => state.session.mySessions,
            sessionsLoading: (state) => state.session.loading,
        }),
        lang: {
            get() {
                return this.$_.get(this.user, "lang");
            },
            set(lang) {
                this.updateMe({ _id: this.user._id, lang }).then(() => {
                    this.updateLang(lang);
                    sendSocketMessage("user lang-change", {
                        userId: this.user._id,
                        siteId: this.site._id,
                        lang: lang,
                    });
                });
            },
        },
        otherSessionsCount() {
            return this.mySessions.filter((s) => !s.isCurrent).length;
        },
        desktopNotificationSettings() {
            return this.notificationSettings.slice(0, 6);
        },
    },
    methods: {
        ...mapActions({
            updateMe: "user/updateMe",
            updateUserPassword: "user/updateUserPassword",
            refreshAuthUser: "auth/refreshAuthUser",
            fetchMySessionsAction: "session/fetchMySessions",
            revokeSession: "session/revokeSession",
            revokeAllOtherSessions: "session/revokeAllOtherSessions",
        }),
        ...mapMutations({
            updateLang: "auth/UPDATE_LANG",
        }),
        getNotificationLabel(type) {
            const labels = {
                new_visitor: "newVisitor",
                new_chat: "newConversation",
                new_conversation: "assignedChat",
                new_message: "newMessage",
                team_chat_team: "teamChatTeam",
                team_chat_dm: "teamChatDm",
                team_chat_department: "teamChatDepartment",
                team_chat_group: "teamChatGroup",
                new_announcement: "newAnnouncement",
                ticket_sound: "newTicketSound",
            };
            return labels[type] || type;
        },
        getNotificationIcon(type) {
            const icons = {
                new_visitor: "user-add",
                new_chat: "message",
                new_conversation: "team",
                new_message: "mail",
                new_announcement: "notification",
                ticket_sound: "file-text",
            };
            return icons[type] || "bell";
        },
        playPreviewSound(settings) {
            let path = settings.soundFile;
            let audio = new Audio(`./static/media/ogg_folder/${path}.ogg`);
            audio.volume = settings.audioLevel / 100;
            audio.play().catch(() => {
                this.$message.error(this.$t("message.chooseAnOption"));
            });
        },
        updateNotificationSettings: _.debounce(async function (notificationSettings) {
            this.user.notificationSettings = notificationSettings;
            await this.updateMe({ ...this.user });
            sendSocketMessage("user user-updated", {
                userId: this.user._id,
                siteId: this.site._id,
            });
            await this.refreshAuthUser();
            await this.$toasted.show(this.$t("message.userVolumeUpdated"), {
                duration: 3000,
                position: "bottom-center",
            });
        }, 500),
        changeUserName() {
            this.updateMe({ _id: this.user._id, name: this.userName }).then(() => {
                this.$toasted.show(this.$t("message.userSaved"), {
                    duration: 3000,
                    position: "bottom-center",
                });
                sendSocketMessage("user user-updated", {
                    userId: this.user._id,
                    siteId: this.site._id,
                });
            });
        },
        changePassword() {
            if (this.newPassword !== this.passwordConfirmation) {
                this.$toasted.error(this.$t("message.passwordMismatch"), {
                    duration: 3000,
                    position: "bottom-center",
                });
                return;
            }
            if (this.newPassword.length < 6) {
                this.$toasted.error(this.$t("message.passwordAtLeast", { num: "6" }), {
                    duration: 3000,
                    position: "bottom-center",
                });
                return;
            }
            this.updateUserPassword({
                id: this.user._id,
                oldPassword: this.oldPassword,
                newPassword: this.newPassword,
            })
                .then(() => {
                    this.oldPassword = "";
                    this.newPassword = "";
                    this.passwordConfirmation = "";
                    this.$toasted.show(this.$t("message.passwordChangeSuccess"), {
                        duration: 5000,
                        position: "bottom-center",
                    });
                })
                .catch(() => {
                    this.$toasted.error(this.$t("message.oldPasswordError"), {
                        duration: 5000,
                        position: "bottom-center",
                    });
                });
        },
        beforeUpload(file) {
            const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/jpg" || file.type === "image/png";
            if (!isJpgOrPng) {
                this.$toasted.error("Sadece Resim Yükleyebilirsiniz!", { duration: 3000 });
            }
            const isLt2M = file.size / 1024 / 1024 < 20;
            if (!isLt2M) {
                this.$toasted.error("Resminizin Boyutu En Fazla 20MB Olmalıdır!", { duration: 3000 });
            }
            return isJpgOrPng && isLt2M;
        },
        async uploadFile(e) {
            let { files: [file] } = document.getElementById(e.target.id);
            if (!this.beforeUpload(file)) return;
            let path = `${this.site._id}/avatar/${this.user._id}`;
            try {
                let uploadData = await uploadAsset({
                    path,
                    mime: file.type,
                    name: file.name,
                    size: file.size,
                    type: "user-avatar",
                });
                const gravatarUrl = uploadData.data.asset.v2Format && uploadData.data.asset.cdnUrl
                    ? uploadData.data.asset.cdnUrl
                    : `https://static.supsis.live/${path + uploadData.data.asset._id}`;

                this.updateMe({ ...this.user, gravatar: gravatarUrl });

                await axios({
                    method: uploadData.data.method,
                    url: uploadData.data.url,
                    data: file,
                    headers: { "Content-Type": "application/octet-stream" },
                });

                this.$toasted.success(this.$t("message.successImgUpload"), {
                    duration: 1000,
                    position: "bottom-center",
                });
                this.gravatar = gravatarUrl;
                this.refreshAuthUser();
            } catch (e) {
                this.$toasted.error(this.$t("message.errorImgUpload"), {
                    duration: 1000,
                    position: "bottom-center",
                });
            }
        },
        fetchMySessions() {
            this.fetchMySessionsAction();
        },
        handleRevoke(sessionId) {
            this.$dialog
                .confirm(this.$t("sessions.revokeConfirm"), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                })
                .then(() => {
                    this.revokeSession(sessionId)
                        .then(() => {
                            this.$toasted.show(this.$t("sessions.revokeSuccess"), {
                                duration: 3000,
                                position: "bottom-center",
                            });
                        })
                        .catch(() => {
                            this.$toasted.error(this.$t("sessions.revokeFailed"), {
                                duration: 3000,
                                position: "bottom-center",
                            });
                        });
                });
        },
        handleRevokeAll() {
            this.$dialog
                .confirm(this.$t("sessions.revokeAllConfirm"), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                })
                .then(() => {
                    this.revokeAllOtherSessions()
                        .then(() => {
                            this.$toasted.show(this.$t("sessions.revokeAllSuccess"), {
                                duration: 3000,
                                position: "bottom-center",
                            });
                        })
                        .catch(() => {
                            this.$toasted.error(this.$t("sessions.revokeFailed"), {
                                duration: 3000,
                                position: "bottom-center",
                            });
                        });
                });
        },
    },
    watch: {
        activeTab(newTab) {
            if (newTab === "sessions") {
                this.fetchMySessions();
            }
        },
        notificationSettings: {
            handler(newValue) {
                if (newValue && !this.isFirstOpening) {
                    this.updateNotificationSettings(newValue);
                }
            },
            deep: true,
        },
    },
    mounted() {
        if (this.user) {
            this.userName = this.user.name;
            if (this.user.notificationSettings) {
                this.notificationSettings = this.notificationSettings.map((x) => {
                    let md = this.user.notificationSettings.find((xx) => xx.type === x.type);
                    if (md) return { ...x, ...md };
                    return x;
                });
            }
            this.$nextTick(() => {
                this.isFirstOpening = false;
            });
        }
    },
};
</script>

<style lang="scss" scoped>
.profile-page {
    
}

.profile-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 12px;
    padding: 20px 24px;
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 16px;

    &__avatar {
        flex-shrink: 0;
    }

    &__info {
        flex: 1;
    }

    &__name {
        color: #fff;
        font-size: 20px;
        font-weight: 600;
        margin: 0 0 4px 0;
    }

    &__email {
        color: rgba(255, 255, 255, 0.85);
        font-size: 14px;
        margin: 0 0 8px 0;
    }

    &__badges {
        display: flex;
        gap: 8px;
    }
}

.avatar-upload {
    cursor: pointer;
    position: relative;
    width: 80px;
    height: 80px;
    display: block;

    .ant-avatar {
        border: 3px solid rgba(255, 255, 255, 0.3);
    }

    &:hover .avatar-overlay {
        opacity: 1;
    }

    .avatar-overlay {
        position: absolute;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
        z-index: 2;
        background-color: rgba(0, 0, 0, 0.55);
        opacity: 0;
        transition: all 0.3s ease-in-out;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;

        .md-icon {
            color: #fff;
            font-size: 24px !important;
        }
    }
}

.profile-tabs {
    ::v-deep .ant-tabs-nav {
        margin-bottom: 0;

        .ant-tabs-tab {
            .anticon {
                margin-right: 8px;
            }
        }
    }
}

.tab-content {
    padding: 16px;
}

.settings-section {
    margin-bottom: 20px;
}

.section-title {
    font-size: 16px;
    font-weight: 600;
    color: var(--color-text-strong-default, #333);
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 8px;

    .anticon {
        color: #667eea;
    }
}

.section-desc {
    color: var(--color-text-default, #666);
    font-size: 14px;
    margin: 0;
}

.password-form {
    max-width: 350px;
}

// Sound Settings
.sound-settings-grid {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.sound-setting-item {
    background: var(--color-bg-subtle, #f8f9fa);
    border-radius: 6px;
    padding: 10px 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px;

    &__header {
        min-width: 150px;
    }

    &__title {
        font-weight: 500;
        color: var(--color-text-strong-default, #333);
    }

    &__controls {
        display: flex;
        align-items: center;
        gap: 10px;
        flex-wrap: wrap;
    }
}

.volume-control {
    display: flex;
    align-items: center;
    gap: 8px;
}

.volume-label {
    min-width: 40px;
    text-align: right;
    color: var(--color-text-default, #666);
    font-size: 13px;
}

// Desktop Notifications
.desktop-notifications-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 10px;
}

.notification-toggle-item {
    background: var(--color-bg-subtle, #f8f9fa);
    border-radius: 6px;
    padding: 10px 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    &__info {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .notification-icon {
        font-size: 18px;
        color: #667eea;
    }
}

// Sessions
.sessions-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 16px;
    flex-wrap: wrap;
    gap: 12px;
}

.sessions-actions {
    display: flex;
    gap: 8px;
}

.sessions-loading,
.sessions-empty {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 150px;
}

.sessions-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

@media (max-width: 768px) {
    .profile-header {
        flex-direction: column;
        text-align: center;
        padding: 16px;
    }

    .profile-header__badges {
        justify-content: center;
    }

    .sessions-header {
        flex-direction: column;
    }

    .sessions-actions {
        width: 100%;
        justify-content: flex-end;
    }
}
</style>
